import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Separator } from '../components/ui/separator';
import { TrendingUp, RefreshCw, ChevronRight, Calendar, Users, UtensilsCrossed, Building2, AlertTriangle, Clock, Send } from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { format, parseISO } from 'date-fns';

const fmt = (n) => n != null ? `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}` : 'N/A';
const fmtDate = (d) => { try { return format(parseISO(d), 'MMM d'); } catch { return 'N/A'; } };

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [followups, setFollowups] = useState([]);
  const [dueOutreach, setDueOutreach] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, fuRes, outRes] = await Promise.all([
        api.get('/api/dashboard/stats'),
        api.get('/api/contacts/followups/due'),
        api.get('/api/outreach/due'),
      ]);
      setStats(statsRes.data);
      setFollowups(fuRes.data.slice(0, 8));
      setDueOutreach(outRes.data.slice(0, 5));
    } catch {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const kpiCards = [
    { label: 'Total Contacts', value: stats?.total_contacts || 0, icon: Users, color: 'text-blue-600', path: '/contacts' },
    { label: 'Restaurants', value: stats?.total_restaurants || 0, icon: UtensilsCrossed, color: 'text-amber-600', path: '/restaurants' },
    { label: 'Spaces', value: stats?.total_spaces || 0, icon: Building2, color: 'text-teal-600', path: '/spaces' },
    { label: 'Overdue Follow-ups', value: stats?.overdue_followups || 0, icon: AlertTriangle, color: 'text-red-500', path: '/contacts', urgent: (stats?.overdue_followups || 0) > 0 },
    { label: 'Due Today', value: stats?.due_today || 0, icon: Clock, color: 'text-amber-500', path: '/contacts' },
    { label: 'Active Sequences', value: stats?.active_sequences || 0, icon: Send, color: 'text-primary', path: '/outreach' },
  ];

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div className="page-header flex items-start justify-between">
        <div>
          <h1 className="page-title">Good morning, {user?.name?.split(' ')[0] || 'Sam'} 🏖️</h1>
          <p className="page-subtitle">{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
        </div>
        <Button onClick={loadData} variant="outline" size="sm" data-testid="dashboard-refresh">
          <RefreshCw size={14} className="mr-1.5" /> Refresh
        </Button>
      </div>

      {/* KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="dashboard-kpi-grid">
        {kpiCards.map((card) => (
          <button
            key={card.label}
            onClick={() => navigate(card.path)}
            className={`stat-card text-left hover:shadow-md transition-shadow ${card.urgent ? 'border-red-200 bg-red-50' : ''}`}
            data-testid={`dashboard-kpi-${card.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <div className={`flex items-center justify-between mb-2`}>
              <card.icon size={18} className={card.color} style={card.colorStyle} />
              {card.urgent && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
            </div>
            <div className="text-2xl font-bold text-foreground tabular-nums">{card.value}</div>
            <div className="text-xs text-muted-foreground mt-0.5 leading-tight">{card.label}</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Follow-ups Due */}
        <Card data-testid="dashboard-followups-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <AlertTriangle size={16} className="text-amber-500" />
                Follow-ups Due
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/contacts')}>
                View all <ChevronRight size={14} className="ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {followups.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <Clock size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">All caught up! No follow-ups due.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {followups.map((c) => (
                  <div key={c.id} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-foreground truncate">{c.name}</div>
                      <div className="text-xs text-muted-foreground truncate">{c.category} {c.restaurant_name ? `· ${c.restaurant_name}` : ''}</div>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <span className="text-xs text-muted-foreground">{c.next_followup_date ? fmtDate(c.next_followup_date) : ''}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        c.followup_status === 'overdue' ? 'badge-overdue' : 'badge-due-today'
                      }`}>
                        {c.followup_status === 'overdue' ? 'Overdue' : 'Due Today'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Outreach Due */}
        <Card data-testid="dashboard-outreach-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Send size={16} className="text-primary" />
                Outreach Due
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/outreach')}>
                View all <ChevronRight size={14} className="ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {dueOutreach.length === 0 ? (
              <div className="text-center py-6 text-muted-foreground">
                <Send size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No outreach sequences due.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {dueOutreach.map((seq) => (
                  <div key={seq.id} className="flex items-center justify-between p-2.5 rounded-lg bg-muted/50 hover:bg-muted transition-colors">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm text-foreground truncate">{seq.contact_name}</div>
                      <div className="text-xs text-muted-foreground">{seq.contact_category} · Stage {seq.current_stage}/4</div>
                    </div>
                    <Badge variant="secondary" className="text-xs ml-2">
                      Day {seq.due_stage?.day || '?'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Top Restaurants */}
      {stats?.top_restaurants?.length > 0 && (
        <Card data-testid="dashboard-top-restaurants">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp size={16} className="text-teal-600" />
                Revenue Intelligence – Top Restaurants
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/restaurants')}>
                View all <ChevronRight size={14} className="ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-2">
              {stats.top_restaurants.map((r, i) => (
                <div key={r.id} className="flex items-center gap-3 p-2.5 rounded-lg bg-muted/50">
                  <span className="text-sm font-bold text-muted-foreground w-5">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-foreground truncate">{r.name}</div>
                    <div className="text-xs text-muted-foreground">{r.village}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-semibold text-foreground tabular-nums">{fmt(r.monthly_seasonal)}/mo</div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium badge-${r.financial_health?.toLowerCase()}`}>
                      {r.financial_health}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Contact breakdown */}
      {stats?.contact_counts && (
        <Card data-testid="dashboard-contact-breakdown">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Users size={16} className="text-blue-600" />
              Contact Portfolio Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
              {Object.entries(stats.contact_counts).map(([cat, count]) => (
                <button
                  key={cat}
                  onClick={() => navigate(`/contacts?category=${encodeURIComponent(cat)}`)}
                  className="text-center p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                >
                  <div className="text-xl font-bold text-foreground tabular-nums">{count}</div>
                  <div className="text-xs text-muted-foreground mt-0.5 leading-tight">{cat}</div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
